import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import
{
    WebContact,
    WebCustomer,
    WebSelectionItem,
    ContactWeb,
    WebCompany,
    CompanyClient,
    WebNotificationSettings,
    WebSettings,
} from "src/app/models";
import
{
    ContactService,
    CustomerService,
    UtilityService,
    CompanyService,
    SharedService,
} from "src/app/services";
import { Subscription } from "rxjs";
import { ErrorDialogComponent } from "../../../components/error-dialog/error-dialog.component";
import { ContactListDialogComponent } from "../contact-list-dialog/contact-list-dialog.component";
import { AlertDialogComponent } from "src/app/components/alert-dialog/alert-dialog.component";
import { SessionDataService } from "src/app/services/session-data.service";
import { filter } from "rxjs/operators";

@Component({
    selector: "app-contact-dialog",
    templateUrl: "./contact-dialog.component.html",
    styleUrls: ["./contact-dialog.component.scss"],
})
export class ContactDialogComponent implements OnInit, OnDestroy
{
    public form: FormGroup;

    public contact: WebContact;
    public contactWeb: ContactWeb;

    private phoneDescriptionsSubscription: Subscription;
    public phoneDescriptions: WebSelectionItem[];

    isTextingActive: boolean;
    isEmailingActive: boolean;
    isECommerceActive: boolean;
    areContactsEditable: boolean = true;
    startupSettings: any;
    canEditNotifications: boolean = true;
    showTypeError: boolean = true;

    dataLogical: string;

    isSubmitted: boolean = false;
    showCustHeader: boolean = true;
    requireEmail: boolean = false;
    requirePhoneNo: boolean = false;
    allowEmails: boolean;
    allowTexts: boolean;
    isEmployee: boolean;

    private initialToggleStates: { [key: string]: any };
    public customerSubscription: Subscription;
    public customer: WebCustomer;
    public company: WebCompany;
    public companySubscription: Subscription;
    private communicationsSubscription: Subscription;
    public webSettings: WebSettings;
    public webSettingsSubscription: Subscription;
    originalNotificationsData: WebNotificationSettings = {};
    notificationsData: WebNotificationSettings = { ...this.originalNotificationsData };

    constructor(
        private fb: FormBuilder,
        private companyClient: CompanyClient,
        public sharedService: SharedService,
        private contactService: ContactService,
        private sessionDataService: SessionDataService,
        private utilityService: UtilityService,
        private customerService: CustomerService,
        private companyService: CompanyService,
        public dialog: MatDialog,
        public dialogRef: MatDialogRef<ContactDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: WebContact
    )
    {
        this.contact = { ...data };
    }

    public ngOnInit(): void
    {
        this.customerSubscription = this.customerService.customer
            .pipe(filter(customer => !!customer))
            .subscribe(customer =>
            {
                this.customer = customer;
            });

        this.companySubscription = this.companyService.company.subscribe((e) =>
        {
            this.company = e;
        });

        this.phoneDescriptionsSubscription = this.utilityService.phoneDescriptions.subscribe((p) =>
        {
            this.phoneDescriptions = p;
        });

        this.dataLogical = this.sessionDataService.DataLogical;
        this.isEmployee = this.sessionDataService.IsEmployee;

        this.webSettingsSubscription = this.companyService.webSettings.subscribe((w) =>
        {
            if (w)
            {
                this.areContactsEditable = w.AreContactsEditable ?? true;
                this.canEditNotifications = this.areContactsEditable || this.isEmployee;
            }
        });

        this.communicationsSubscription = this.companyClient
            .getAppStartup(this.dataLogical, this.customer.BranchNo)
            .subscribe((c) =>
            {
                this.isTextingActive =
                    c.CompanyInterfaces.IsSMSNotificationsActive && this.canEditNotifications;
                this.isEmailingActive =
                    c.CompanyInterfaces.IsSMTPNotificationsActive && this.canEditNotifications;
                this.isECommerceActive = c.CompanyInterfaces.IsECommerceActive && this.canEditNotifications;
            });

        this.form = this.fb.group({
            Key: [this.contact.Key, []],
            FirstName: [this.contact.FirstName, [Validators.required]],
            LastName: [this.contact.LastName, []],
            PhoneDesc: [this.contact.PhoneDesc, []],
            PhoneCountryCode: [this.contact.PhoneCountryCode, []],
            PhoneNumber: [this.contact.PhoneNumber, []],
            PhoneExt: [this.contact.PhoneExt, []],
            ReqPhoneNo: [this.requirePhoneNo, []],
            Type: [this.contact.Type, []],
            Email: [this.contact.Email, [Validators.email]],
            ReqEmail: [this.requireEmail, [Validators.required]],
            AllowEmailMsg: [this.contact.AllowEmailMsg, []],
            AllowPhoneTextMsg: [this.contact.AllowPhoneTextMsg, []],
            DefaultInvoice: [this.contact.DefaultInvoice, []],
            DefaultStatements: [this.contact.DefaultStatements, []],
            DefaultLetters: [this.contact.DefaultLetters, []],
        });

        this.getInitialTogglesState();
    }

    getInitialTogglesState(): void
    {
        this.initialToggleStates = {
            AllowEmailMsg: this.form.get("AllowEmailMsg").value,
            AllowPhoneTextMsg: this.form.get("AllowPhoneTextMsg").value,
            DefaultLetters: this.form.get("DefaultLetters").value,
            DefaultStatements: this.form.get("DefaultStatements").value,
            DefaultInvoice: this.form.get("DefaultInvoice").value,
        };
        this.updateValidation();
    }

    textSelected(checked: boolean): boolean
    {
        this.allowTexts = checked;
        this.updateValidation();
        return this.allowTexts;
    }

    emailSelected(checked: boolean): boolean
    {
        this.allowEmails = checked;
        this.updateValidation();
        return this.allowEmails;
    }

    updateValidation(): void
    {
        this.requireEmail = this.form.get('AllowEmailMsg').value ||
            this.form.get('DefaultLetters').value ||
            this.form.get('DefaultStatements').value ||
            this.form.get('DefaultInvoice').value ||
            this.contactService.checkEmails();
        this.form.get('ReqEmail').setValue(this.requireEmail);

        this.requirePhoneNo = this.form.get('AllowPhoneTextMsg').value;
        this.form.get('ReqPhoneNo').setValue(this.requirePhoneNo);
    }

    public handleSubmit(): void
    {
        this.showCustHeader = false;
        this.isSubmitted = true;
        if (this.form.invalid)
        {
            this.isSubmitted = false;
            const dialogRef = this.dialog.open(AlertDialogComponent, {
                panelClass: "form-field-less-than-180",
                width: "500px",
                height: "200px",
                data: {
                    title: "Invalid Form",
                    message: "Please fill in all required fields before submitting.",
                    buttonText: "Close",
                },
            });
            dialogRef.afterClosed().subscribe(() =>
            {
                this.showCustHeader = true;
                this.isSubmitted = false;
            });
            return;
        }
        const result = this.form.dirty ? Object.assign(this.contact, this.form.value) : null;
        if (result)
        {
            this.contactService.updateContact(result).subscribe(
                (c) =>
                {
                    if (this.didTogglesChange())
                    {
                        this.notificationsData.Company = this.company;
                        this.notificationsData.Customer = this.customer;
                        this.notificationsData.Contact = this.form.value;
                        this.utilityService.sendNotificationsEmail(this.notificationsData).subscribe((r) => { });
                    }
                    this.dialog.closeAll();
                    if (!c)
                    {
                        let errorMessage = "Your contact was not saved. Please try again.";
                        this.dialog.open(ErrorDialogComponent, {
                            minWidth: "360px",
                            data: errorMessage,
                        });
                    }
                },
                (error) =>
                {
                    this.errorDialog(error);
                }
            );
        } 
        else
        {
            this.dialog.closeAll();
        }
        this.routeUser();
    }

    async routeUser(): Promise<void>
    {
        this.dialog.closeAll();
        await new Promise((r) => setTimeout(r, 1000));
        if (this.contactService.checkEmails)
        {
            this.dialog.open(ContactListDialogComponent, {
                disableClose: true,
                width: "600px",
                maxHeight: "90%",
                maxWidth: "90%",
            });
        }
    }

    private didTogglesChange(): boolean
    {
        const currentToggleStates = {
            AllowEmailMsg: this.form.get("AllowEmailMsg")?.value,
            AllowPhoneTextMsg: this.form.get("AllowPhoneTextMsg")?.value,
            DefaultLetters: this.form.get("DefaultLetters")?.value,
            DefaultStatements: this.form.get("DefaultStatements")?.value,
            DefaultInvoice: this.form.get("DefaultInvoice")?.value,
        };

        return !this.compareObjects(this.initialToggleStates, currentToggleStates);
    }

    compareObjects(o1: any, o2: any): boolean
    {
        return JSON.stringify(o1) === JSON.stringify(o2);
    }

    errorDialog(error): void
    {
        const errorMessage: string =
            "Your contact was not saved. Please try again. \n Reason: " + error;

        const dialogRef = this.dialog.open(ErrorDialogComponent, {
            minWidth: "360px",
            data: errorMessage,
        });

        dialogRef.afterClosed().subscribe((_) =>
        {
            this.isSubmitted = false;
            this.showCustHeader = true;
        });
    }

    public ngOnDestroy(): void
    {
        this.phoneDescriptionsSubscription.unsubscribe();
        this.customerSubscription.unsubscribe();
        this.companySubscription.unsubscribe();
        this.communicationsSubscription.unsubscribe();
        this.webSettingsSubscription.unsubscribe();
    }
}
